import type { ExperimentsConfig } from '../models/config'
import { Experiment } from './experiment'
import { getStoredKey } from '../utils/storage'

export class Experiments {
  private experiments: Record<string, Experiment<any>> = {}

  /**
   * @param config - The configuration object containing experiments.
   */
  constructor(config: ExperimentsConfig) {
    for (const [name, variations] of Object.entries(config)) {
      this.experiments[name] = new Experiment(name, { variations })
    }
  }

  /**
   * Parses and applies query overrides for experiments.
   */
  private getExperimentBranchFromQuery(): void {
    const urlParams = new URLSearchParams(window.location.search);

    for (const [name, value] of urlParams.entries()) {

      const experimentName = name
        .replace(/^experiments\[|]$/g, '')
        .replace(/^'(.*)'$/, '$1')

      const experiment = this.experiments[experimentName]

      if (experiment) {
        const success = experiment.overrideVariation(value)
        if (!success) {
          console.warn(`Invalid override value "${value}" for experiment "${experimentName}".`)
        }
      }
    }
  }

  /**
   * Runs a specific experiment by its name.
   * Applies query overrides before running the experiment.
   *
   * @param name - The name of the experiment.
   * @returns The value of the selected variation for the specified experiment.
   */
  getExperimentBranch(name: string): { value: 'original' | 'test' | undefined; isTest: boolean } {
    this.getExperimentBranchFromQuery()

    const experiment = this.experiments[name]

    if (!experiment) {
      console.error(`Experiment with name "${name}" does not exist.`)
      return {
        value: undefined,
        isTest: false
      }
    }
    return {
      value: experiment.getVariation(),
      isTest: experiment.getVariation() === 'test'
    }
  }

  /**
   * Returns the value for a specific experiment by its name.
   *
   * @param name - The name of the experiment.
   * @returns The value of the selected variation for the specified experiment, or undefined if it does not exist.
   */
  getValue(name: string): any {
    const experiment = this.experiments[name]
    if (experiment) {

      const savedValue = localStorage.getItem(
        getStoredKey(name)
      )

      return savedValue
        ? JSON.parse(savedValue)
        : undefined
    }
    return undefined
  }

  /**
   * Returns a map of all experiment names to their selected variation values.
   * Applies query overrides before resolving.
   *
   * @returns Record of experiment name => selected variation value.
   */
  getAllValues(): Record<string, any> {
    this.getExperimentBranchFromQuery()

    const result: Record<string, any> = {}

    for (const [name, experiment] of Object.entries(this.experiments)) {
      result[name] = experiment.getVariation()
    }

    return result
  }

  /**
   * Runs all experiments and returns a map of their values.
   * Useful for preloading experiment states (e.g., at app start).
   */
  runAll(): Record<string, { value: any; isTest: boolean }> {
    this.getExperimentBranchFromQuery()

    const result: Record<string, { value: any; isTest: boolean }> = {}

    for (const [name, experiment] of Object.entries(this.experiments)) {
      const value = experiment.getVariation()
      result[name] = {
        value,
        isTest: value === 'test'
      }
    }

    return result
  }

  /**
   * Resets a specific experiment by clearing its results from localStorage.
   * This effectively resets the state of the specified experiment, allowing it to be re-run as new.
   *
   * @param name - The name of the experiment to reset.
   */
  reset(name: string): void {
    const experiment = this.experiments[name]
    if (experiment) {
      experiment.resetVariation()
    } else {
      console.warn(`Experiment with name "${name}" does not exist.`)
    }
  }
}
